import Clarity from "@microsoft/clarity";

import { ConfigProvider } from "antd";
import "bootstrap/dist/css/bootstrap.min.css";
import React, { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter as Router, RouterProvider, Routes, Route } from "react-router-dom";
import { APP_REACT_URL } from "./constants/AppConstants";
import "./index.css";
import createRouter from "./pages/router";
import { isServiceGetStartedPath } from "./pages/utils/commonUtils";
import { CommonLoadingV2 } from "./pages/utils/components/HSLoadingComponents";
import antdConfig from "./antdConfig";
import useTabTitle from "./hooks/useTabTitle";
import { ProviderObj } from "./context/AppContext";

const BackgroundTaskWrapper = lazy(() => import("./components/main/BackgroundTaskWrapper"));
const SurveyWrapper = lazy(() => import("./survey/Survey"));
const AdminWrapper = lazy(() => import("./components/main/AdminWrapper"));
const OTPModal = lazy(() => import("./components/custom/OTPModal"));
const InfoModal = lazy(() => import("./components/custom/modal/info/InfoModal"));
const Pricingbar = lazy(() => import("./components/main/pricingbar"));
const ServiceGetStartedPage = lazy(() => import("./pages/services/ServiceGetStartedPage"));
const PermissionDeniedModal = lazy(() => import("./pages/utils/PermissionDeniedModal"));
const HNotification = lazy(() => import("./components/custom/notification/HNotification"));
const PusherProvider = lazy(() => import("./context/PusherContext").then((module) => ({ default: module.PusherProvider })));
const RolesProvider = lazy(() => import("./context/RolesContext").then((module) => ({ default: module.RolesProvider })));
const EscHandlerProvider = lazy(() => import("./context/EscHandlerContext").then((module) => ({ default: module.EscHandlerProvider })));
const VoiceProvider = lazy(() => import("./voice/context/VoiceContext").then((module) => ({ default: module.VoiceProvider })));
const AgentStatusSyncListener = lazy(() =>
  import("./components/main/AgentStatusSyncListener").then((module) => ({ default: module.AgentStatusSyncListener }))
);
const BeamsProvider = lazy(() => import("./context/BeamsContext").then((module) => ({ default: module.BeamsProvider })));

const searchParams = new URLSearchParams(window.location.search);
const isInternal = searchParams.get("internal") === "true";
const pathNameArray = window.location.pathname.replace(/^\/+/, "").split("/");
const isAdminTool = pathNameArray?.[0] === "admin";
const stopThirdParty = isInternal || isAdminTool || pathNameArray.includes("sendsms") || pathNameArray.includes("dialpad");

const App = () => {
  let pathName = window.location.pathname;
  const pathSegments = pathName.split("/");
  const [pricing] = useState(pathSegments?.[1] === "pricing" || pathSegments?.[1] === "package");
  const isServiceGetStartedPage = isServiceGetStartedPath(pathName);
  const [permissionDeniedToSubAccount, setPermissionDeniedToSubAccount] = useState(false);
  const [admin] = useState(pathSegments?.indexOf("admin") !== -1 ? true : false);
  const [survey] = useState(pathSegments?.indexOf("survey") !== -1 ? true : false);
  const [isBackgroundTask] = useState(pathSegments?.indexOf("background-task") !== -1 ? true : false);

  const [otpModalState, setOtpModalState] = useState(false);
  const [showCaptchaWidget, setShowCaptchaWidget] = useState(false);
  const [blockInteractionModalProps, setBlockInteractionModalProps] = useState({
    props: {},
  });
  const [accessDenied, setAccessDenied] = useState(false);
  const [appPropsObject, setAppPropsObject] = useState({});
  const [loading, setLoading] = useState(
    pathSegments?.indexOf("content") !== -1 ? false : true
  );

  const loadingContextValue = {
    setLoading: setLoading,
    loading: loading,
    isAppContextCreated: appPropsObject?.isAppContextCreated,
  };

  // Update tab title with notification count
  useTabTitle();

  useEffect(() => {
    if (!stopThirdParty && process.env.REACT_APP_ENV !== "development") {
      // Defer third-party scripts loading until after page load
      const loadThirdPartyScripts = () => {
        const projectId = process.env.REACT_APP_ENV === "production-1" ? "nd80pjlpbf" : "pygp7sgnfn";
        Clarity.init(projectId);

        const pagesenseScript = document.createElement("script");
        pagesenseScript.src = "https://cdn.pagesense.io/js/deskoappsxyz/ffc2aa6d77c8431d8479ddae3f8efc28.js";
        pagesenseScript.async = true;
        document.body.appendChild(pagesenseScript);
        var hasSideBarFalse = searchParams.get("sideBar") === "false";
        var isLandingPage = pathName.includes("/landing");

        var containsPreviousPath = searchParams.get("prevPath") !== null;

        // Disable if path is in disabled list OR (sideBar=false AND not a landing page) OR isInternalPage
        var isTidioDisabled = (hasSideBarFalse && (!isLandingPage || containsPreviousPath)) || navigator.userAgent.includes("HelloSendMobile");

        if (!isTidioDisabled) {
          const tidioScript = document?.createElement("script");
          tidioScript.src = "https://code.tidio.co/gycvgq9nlsxmzsqyquclxsmfbloiyfao.js";
          tidioScript.async = true;
          document?.body?.appendChild(tidioScript);
        }


        const tidioChatWidgetElement = document.getElementById("tidio-chat");
        const tidioChatWidgetShadowRoot = tidioChatWidgetElement?.shadowRoot;

        const style = document.createElement("style");
        style.textContent = `
             #button.sidebar {
                 bottom: calc(25% - 100px) !important;
              } `;

        if (tidioChatWidgetShadowRoot) {
          tidioChatWidgetShadowRoot.appendChild(style);
        }

        return () => {
          const existingSleekScript = document.querySelector('script[src*="sleekplan.com"]');
          const existingPagesenseScript = document.querySelector('script[src*="pagesense.io"]');
          if (existingSleekScript) document.body.removeChild(existingSleekScript);
          if (existingPagesenseScript) document.body.removeChild(existingPagesenseScript);
        };
      };

      // Use requestIdleCallback for better performance, fallback to setTimeout
      if (window.requestIdleCallback) {
        window.requestIdleCallback(loadThirdPartyScripts, { timeout: 2000 });
      } else {
        // Fallback: Load after a short delay to ensure page has rendered
        setTimeout(loadThirdPartyScripts, 2000);
      }

      // Also load after window load event as additional safety
      if (document.readyState === "complete") {
        loadThirdPartyScripts();
      } else {
        window.addEventListener("load", loadThirdPartyScripts, { once: true });
      }

      return () => {
        const existingSleekScript = document.querySelector('script[src*="sleekplan.com"]');
        const existingPagesenseScript = document.querySelector('script[src*="pagesense.io"]');
        if (existingSleekScript) document.body.removeChild(existingSleekScript);
        if (existingPagesenseScript) document.body.removeChild(existingPagesenseScript);
      };
    }
  }, []);
  const router = createRouter(loadingContextValue, null, accessDenied, setAccessDenied);
  return (
    <>
      {pricing ? (
        <Router>
          <Suspense fallback={null}>
            <Pricingbar setOtpModalState={setOtpModalState} otpModalState={otpModalState} />
          </Suspense>
        </Router>
      ) : isServiceGetStartedPage ? (
        <Router>
          <Suspense fallback={<CommonLoadingV2 />}>
            <Routes>
              <Route path="/web/get-started/:serviceName" element={<ServiceGetStartedPage />} />
            </Routes>
          </Suspense>
        </Router>
      ) : admin ? ( // Router><Admin/></Router>
        <Suspense fallback={null}>
          <AdminWrapper />
        </Suspense>
      ) : survey ? (
        <Suspense fallback={null}>
          <SurveyWrapper />
        </Suspense>
      ) : isBackgroundTask ? (
        <Suspense fallback={null}>
          <BackgroundTaskWrapper />
        </Suspense>
      ) : (
        <>
          <ConfigProvider theme={antdConfig}>
            {/* {notificationContextHolder} */}
            <Suspense fallback={null}>
              <HNotification />
            </Suspense>
            {otpModalState && (
              <Suspense fallback={null}>
                <OTPModal
                  appPropsObject={appPropsObject}
                  open={otpModalState}
                  setLoading={setLoading}
                  setOtpModalState={setOtpModalState}
                  showCaptchaWidget={showCaptchaWidget}
                  setShowCaptchaWidget={setShowCaptchaWidget}
                />
              </Suspense>
            )}
            <Suspense fallback={null}>
              <PermissionDeniedModal permissionDeniedToSubAccount={permissionDeniedToSubAccount} />
            </Suspense>
            <Suspense fallback={null}>
              <EscHandlerProvider>
                <RolesProvider>
                  <ProviderObj
                    setLoading={setLoading}
                    setBlockInteractionModalProps={setBlockInteractionModalProps}
                    setOtpModalState={setOtpModalState}
                    setPermissionDeniedToSubAccount={setPermissionDeniedToSubAccount}
                    setAppPropsObject={setAppPropsObject}
                    setAccessDenied={setAccessDenied}
                    accessDenied={accessDenied}
                    setShowCaptchaWidget={setShowCaptchaWidget}
                    showCaptchaWidget={showCaptchaWidget}
                  >
                    {loading ? <CommonLoadingV2 /> : null}
                    <BeamsProvider>
                      <PusherProvider>
                        <VoiceProvider>
                          <Suspense fallback={null}>
                            <AgentStatusSyncListener />
                          </Suspense>
                          <RouterProvider router={router} />
                        </VoiceProvider>
                      </PusherProvider>
                    </BeamsProvider>
                    {blockInteractionModalProps?.modalKey && (
                      <BlockInteractionModalByKey modalKey={blockInteractionModalProps.modalKey} props={blockInteractionModalProps.props} />
                    )}
                  </ProviderObj>
                </RolesProvider>
              </EscHandlerProvider>
            </Suspense>
          </ConfigProvider>
        </>
      )}
    </>
  );
};

function BlockInteractionModalByKey({ modalKey, props }) {
  if (modalKey.includes("campaign-success")) {
    return (
      <Suspense fallback={null}>
        <InfoModal
          content={props?.message}
          title={props?.title}
          modalClassName="modal-success"
          type="success"
          open={true}
          okButtonText={props?.buttonText}
          handleOk={() => {
            window.open(APP_REACT_URL + props?.buttonHref, "_blank");
          }}
        />
      </Suspense>
    );
  }
  return null;
}

export default App;
